import React from 'react';

import { cssMap } from '@compiled/react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import {
	aiConfigItemMessages,
	aiLanguageMessages,
	aiMessages,
} from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import {
	TOOLBAR_RANK,
	AI_SECTION_RANK,
	ROVO_GROUP_RANK,
	ROVO_MENU_RANK,
	IMPROVE_WRITING_GROUP_RANK,
	HERO_PROMPT_BUTTON_GROUP_RANK,
	TOOLBARS,
	AI_SECTION,
	ROVO_GROUP,
	ROVO_GROUP_VIEW,
	ROVO_MENU,
	ROVO_PROMPT_MENU_SECTION_RANK,
	CHANGE_TONE_MENU_RANK,
	CHANGE_TONE_MENU_SECTION_RANK,
	TRANSLATE_MENU_RANK,
	IMPROVE_WRITING_GROUP,
	HERO_PROMPT_BUTTON_GROUP,
	ROVO_HERO_BUTTON,
	ROVO_PROMPT_MENU_SECTION,
	MAKE_SHORTER_MENU_ITEM,
	MAKE_LONGER_MENU_ITEM,
	CHANGE_TONE_NESTED_MENU,
	CHANGE_TONE_MENU_SECTION,
	MORE_PROFESSIONAL_MENU_ITEM,
	MORE_CASUAL_MENU_ITEM,
	MORE_EMPATHETIC_MENU_ITEM,
	SPELLING_AND_GRAMMAR_MENU_ITEM,
	TRANSLATE_NESTED_MENU,
	TRANSLATE_MENU_SECTION,
	IMPROVE_WRITING_BUTTON,
	HERO_PROMPT_BUTTON,
	HERO_PROMPT_MENU_ITEM,
	DEFINE_MENU_ITEM,
	DEFINE_BUTTON,
	DEFINE_GROUP,
	DEFINE_GROUP_RANK,
	AI_VIEW_SECTION,
	IMPROVE_WRITING_MENU_ITEM,
	ROVO_GROUP_DISPLAY_SMALL,
	ROVO_GROUP_DISPLAY_SMALL_RANK,
	ROVO_MENU_DISPLAY_SMALL,
	ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL,
	useEditorToolbar,
	getInputMethodFromParentKeys,
	AI_SECTION_PRIMARY_TOOLBAR,
	ROVO_GROUP_PRIMARY_TOOLBAR,
	ROVO_WRITE_BUTTON_PRIMARY_TOOLBAR,
	AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR,
	AI_SECTION_COMMENT_PRIMARY_TOOLBAR,
	ADD_POLISH_MENU_ITEM,
} from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import {
	MoreItemsIcon,
	AIBriefcaseIcon,
	AICasualIcon,
	AICommandIcon,
	AIHeartIcon,
	AIShortenIcon,
	AISpellcheckIcon,
	AIChangeToneIcon,
	AILengthenIcon,
	ToolbarButton,
	ToolbarDropdownMenu,
	ToolbarNestedDropdownMenu,
	ToolbarTooltip,
	ToolbarDropdownItem,
	ToolbarDropdownMenuProvider,
	NestedDropdownRightIcon,
	AITranslateIcon,
	AIChatIcon,
	Show,
	ToolbarButtonGroup,
	SeparatorPosition,
} from '@atlaskit/editor-toolbar';
import type { RegisterComponent, ToolbarComponentTypes } from '@atlaskit/editor-toolbar-model';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { Resizing } from '@atlaskit/motion/resizing';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';
import { token } from '@atlaskit/tokens';
import { RovoChatHighlightActions } from '@atlassian/conversation-assistant-ui-components/ui/RovoChatHighlightActions';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { CONFIG_ITEM_KEYS } from '../prebuilt/config-items/config-item-keys';
import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemWithOptions,
} from '../prebuilt/config-items/config-items';
import {
	changeToneToProfessional,
	changeToneToCasual,
	changeToneToEmpathetic,
} from '../prebuilt/config-items/prompts/change-tone/change-tone';
import {
	fixSpellingAndGrammar,
	improveWriting,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { createImproveDescription } from '../prebuilt/config-items/prompts/improve-description/improve-description';
import type { GetAdditionalProps } from '../prebuilt/config-items/prompts/improve-description/improve-description';
import { makeLonger } from '../prebuilt/config-items/prompts/make-longer/make-longer';
import { makeShorter } from '../prebuilt/config-items/prompts/make-shorter/make-shorter';
import { addPolish, addPolishWithOptions } from '../prebuilt/config-items/prompts/polish/polish';
import { createTranslationConfigItems } from '../prebuilt/config-items/prompts/translate/translate';
import { getSortedSupportedTranslationLanguages } from '../prebuilt/config-items/utils/translation-utils';
import { SELECTION_CATEGORY } from '../utils/analytics/enums';
import { isNewAifcExperienceEnabled } from '../utils/new-aifc-experience-enabled';
import { getSelectionCategoryUsingState } from '../utils/selection';

import { AISection, AIViewSection } from './utils/section-render';
import { useToolbarButtonViewedAnalytics } from './utils/toolbar-button-analytics';
import { useIsAiEntryPointDisabled } from './utils/use-is-ai-entry-point-disabled';

const rovoButtonStyles = cssMap({
	container: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: {
			minHeight: token('space.400'),
		},
	},
	heroLabelWrapper: {
		display: 'inline-block',
		textAlign: 'start',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
});

const RovoSplitButtonWrapper = ({
	isNewToolbarEnabled,
	isInDocumentStreamingEnabled,
	children,
}: {
	children: React.ReactNode;
	isInDocumentStreamingEnabled?: boolean;
	isNewToolbarEnabled: boolean;
}) =>
	!isNewToolbarEnabled && isInDocumentStreamingEnabled ? (
		<Inline xcss={rovoButtonStyles.container}>{children}</Inline>
	) : (
		children
	);

// Component for Primary Toolbar AI Section with conditional rendering
const PrimaryToolbarAISection = ({
	api,
	children,
	editorAppearance,
	hasSeparator = false,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	children: React.ReactNode;
	editorAppearance: 'full-page' | 'comment';
	hasSeparator?: boolean;
}) => {
	const { editorAppearance: currentAppearance } = useEditorToolbar();
	// Only show for the specified editor appearance
	if (currentAppearance !== editorAppearance) {
		return null;
	}
	return (
		<AISection api={api} hasSeparator={hasSeparator ? SeparatorPosition.END : undefined}>
			{children}
		</AISection>
	);
};

const RovoButton = ({
	aiGlobalOptIn,
	isNewToolbarEnabled,
	editorPluginAIProvider: { baseGenerate, isInDocumentStreamingEnabled },
	parents,
	api,
	triggeredFor,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	isNewToolbarEnabled: boolean;
	parents: ToolbarComponentTypes;
	triggeredFor?: NodeType;
}) => {
	const { formatMessage, locale } = useIntl();
	const isDisabled = useIsAiEntryPointDisabled(api);

	const { editorView, editorAppearance } = useEditorToolbar();

	// This component gets registered twice, once for the full size toolbar and once for the display small toolbar
	// We need to exclude the display small toolbar from the analytics to prevent double counting
	const onDisplaySmall = parents.some((parent) => parent.key === ROVO_GROUP_DISPLAY_SMALL.key);

	useToolbarButtonViewedAnalytics({
		api,
		state: editorView?.state,
		enabled: isInDocumentStreamingEnabled && !onDisplaySmall,
		actionSubjectId: ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON,
	});

	const browser = getBrowserInfo();
	const metaKey = browser.mac ? '⌘' : 'Ctrl';

	const onPrimaryToolbar = parents.at(0)?.key === TOOLBARS.PRIMARY_TOOLBAR;

	// In the new AIFC editor experience the entry-point label is "Edit" instead of "Ask Rovo".
	const isNewAifcExperience = isNewAifcExperienceEnabled();

	let buttonTitle = '';
	// Determine button title based on toolbar location and editor appearance
	if (onPrimaryToolbar) {
		// Primary toolbar: Only show entry-point text in full-page editor when AIFC patch beta is enabled.
		// In the new AIFC editor experience the label is "Edit"; for the standalone
		// `platform_editor_toolbar_aifc` patch-beta experiment it remains "Write".
		if (editorAppearance === 'full-page' && isInDocumentStreamingEnabled) {
			buttonTitle = formatMessage(
				isNewAifcExperience
					? sharedMessages.aiNewAifcEntryPointCallToAction
					: sharedMessages.aiUniversalShortCallToAction,
			);
		}
		// For comment editor or when AIFC patch beta is disabled, show no text
	} else {
		// Inline/floating toolbar: show "Edit" in the new AIFC experience, otherwise "Ask Rovo".
		buttonTitle = formatMessage(
			isNewAifcExperience
				? sharedMessages.aiNewAifcEntryPointCallToAction
				: aiMessages.askRovoToolbarIconTitle,
		);
	}

	return (
		<RovoSplitButtonWrapper
			isNewToolbarEnabled={isNewToolbarEnabled}
			isInDocumentStreamingEnabled={isInDocumentStreamingEnabled}
		>
			<ToolbarTooltip
				content={formatMessage(aiMessages.askRovoToolbarIconTooltipQuickCommand, {
					key: metaKey,
				})}
			>
				<ToolbarButton
					iconBefore={
						<AIChatIcon
							label={formatMessage(
								isNewAifcExperience
									? sharedMessages.aiNewAifcEntryPointCallToAction
									: aiMessages.askRovoToolbarIconTitle,
							)}
						/>
					}
					testId="editor-toolbar__ask-rovo"
					ignoreInteractionDisabled
					isDisabled={isDisabled}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					onClick={(event) => {
						if (!editorView) {
							return;
						}
						const isKeyboardTrigger = event?.nativeEvent?.detail === 0;
						const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';

						// Fire analytics event for button click
						api?.analytics?.actions.fireAnalyticsEvent({
							action: ACTION.CLICKED,
							actionSubject: ACTION_SUBJECT.BUTTON,
							actionSubjectId: ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON,
							eventType: EVENT_TYPE.UI,
							attributes: {
								inputMethod: getInputMethodFromParentKeys(parents),
							},
						});

						const openAIModalCommand = createOpenAIModalCommand({
							state: editorView.state,
							configItem: baseGenerate,
							lastTriggeredFrom: onPrimaryToolbar ? 'mainToolbar' : 'floatingToolbar',
							triggerMethod,
							aiGlobalOptIn,
							locale,
							triggeredFor,
						});

						openAIModalCommand(editorView.state, editorView.dispatch);
					}}
				>
					{buttonTitle}
				</ToolbarButton>
			</ToolbarTooltip>
		</RovoSplitButtonWrapper>
	);
};

const AskRovoChatButton = ({
	api,
	editorPluginAIProvider,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
}) => {
	const publish = usePublish('ai-mate');
	const { editorView } = useEditorToolbar();

	const { product } = editorPluginAIProvider;

	let selectedContentADF = getEmptyADF();

	// convert the selected content to ADF
	const selectedContent = Fragment.from(editorView?.state.selection.content().content);
	if (selectedContent) {
		const doc = defaultSchema.nodes.doc.create({}, selectedContent);
		selectedContentADF = new JSONTransformer().encode(doc) as DocNode;
	}

	const handleClickChat = () => {
		// set the selection to the end of the current selection to remove the selection toolbar
		if (editorView?.dispatch) {
			const tr = editorView.state.tr;
			tr.setSelection(
				TextSelection.create(
					editorView.state.doc,
					Math.min(editorView.state.selection.to, editorView.state.doc.content.size),
				),
			);
			editorView.dispatch(tr);
		}
		api?.core.actions.blur();

		// open rovo chat with the selected content
		publish({
			type: 'chat-new',
			source: 'editor',
			product: product,
			data: {
				dialogues: [],
			},
		});
	};

	return (
		<RovoChatHighlightActions
			selectedContent={selectedContentADF}
			product={product.toLowerCase()}
			onClickChat={handleClickChat}
		/>
	);
};

export const DefineButton = ({
	api,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { editorView } = useEditorToolbar();

	const isInDocumentStreamingEnabled = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) =>
			states.aiExperienceState?.aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
	);

	useToolbarButtonViewedAnalytics({
		api,
		state: editorView?.state,
		enabled: isInDocumentStreamingEnabled,
		actionSubjectId: ACTION_SUBJECT_ID.AI_DEFINITIONS_DEFINE_BUTTON,
	});

	return (
		<ToolbarTooltip content={formatMessage(aiMessages.DefineDropdownIconTitle)}>
			<ToolbarButton
				isDisabled={api?.aiDefinitions?.actions.selectionExceedsMaxWordCount() || false}
				iconBefore={<InformationCircleIcon label="" size="small" />}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onClick={() => {
					api?.aiDefinitions?.actions.openReadingAidsPopup();
				}}
			>
				{formatMessage(aiMessages.DefineDropdownIconTitle)}
			</ToolbarButton>
		</ToolbarTooltip>
	);
};

// Reusable hero prompt button that extracts details from heroConfigItem
export const HeroPromptButton = ({
	aiGlobalOptIn,
	parents,
	api,
	heroConfigItem,
	emptyStateHeroConfigItem,
	isIconOnly = false,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	emptyStateHeroConfigItem?: EditorPluginAIConfigItemWithOptions;
	heroConfigItem: EditorPluginAIConfigItemWithOptions;
	isIconOnly?: boolean;
	parents: ToolbarComponentTypes;
}): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { editorView } = useEditorToolbar();
	const disabled = useIsAiEntryPointDisabled(api);
	const hasVisibleContent = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) => states.aiExperienceState?.hasVisibleContent,
	);
	const consumerEmptyStateOverride = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) => states.aiExperienceState?.consumerEmptyStateOverride,
	);
	const isInDocumentStreamingEnabled = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) =>
			states.aiExperienceState?.aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
	);

	useToolbarButtonViewedAnalytics({
		api,
		state: editorView?.state,
		enabled: isInDocumentStreamingEnabled,
		actionSubjectId: ACTION_SUBJECT_ID.AI_HERO_PROMPT_BUTTON,
	});

	const showEmptyStateHero =
		consumerEmptyStateOverride === undefined ? !hasVisibleContent : consumerEmptyStateOverride;

	const resolvedHero =
		emptyStateHeroConfigItem &&
		showEmptyStateHero &&
		expValNoExposure('jira-smart-replies-v2', 'isEnabled', false)
			? emptyStateHeroConfigItem
			: heroConfigItem;

	const configItem = resolvedHero.config;
	const title = formatMessage(configItem.title);
	const name = configItem.key.toLowerCase().split('_').join('-').split(' ').join('-');
	const testId = `editor-toolbar__${name}`;
	const enabledOnEmptyDoc =
		resolvedHero.triggers.empty.docMinSize === 0 && resolvedHero.triggers.empty.enabled;
	const isSuggestReplyConfigItemEnabled =
		resolvedHero.config.key === CONFIG_ITEM_KEYS.SUGGEST_RESPONSE &&
		fg('draft_reply_contextual_pills_gate');

	let isDisabled = disabled || !hasVisibleContent;

	isDisabled = disabled || (!hasVisibleContent && !enabledOnEmptyDoc);

	// Specific to JSM - Suggest Reply Hero Prompt
	if (isSuggestReplyConfigItemEnabled) {
		isDisabled = disabled || !!hasVisibleContent;
	}

	const onPrimaryToolbar = parents.at(0)?.key === TOOLBARS.PRIMARY_TOOLBAR;

	if (expValNoExposure('jira-smart-replies-v2', 'isEnabled', false)) {
		const Icon = configItem.icon ? configItem.icon({ formatMessage }) : null;
		const heroPromptButton = (
			<ToolbarButton
				isDisabled={isDisabled}
				iconBefore={Icon}
				label={title}
				testId={testId}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
					if (!editorView) {
						return;
					}
					const state = editorView.state;
					const { selection } = state;
					const selectionCategory =
						selection && getSelectionCategoryUsingState(state, [selection.from, selection.to]);
					const hasPartialNonDocumentSelection =
						selectionCategory !== SELECTION_CATEGORY.EMPTY &&
						selectionCategory !== SELECTION_CATEGORY.FULL_DOCUMENT;

					const isImproveDescriptionHero =
						resolvedHero.config.key === CONFIG_ITEM_KEYS.ADD_STRUCTURE;

					const resolvedConfigItem =
						isImproveDescriptionHero &&
						hasPartialNonDocumentSelection &&
						fg('use_improve_writing_on_partial_text_selection')
							? improveWriting()
							: resolvedHero.config;

					const lastTriggeredFrom = onPrimaryToolbar ? 'mainToolbar' : 'floatingToolbar';
					const isKeyboardTrigger = event?.nativeEvent?.detail === 0;
					const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';

					const openAIModalCommand = createOpenAIModalCommand({
						state: editorView.state,
						configItem: resolvedConfigItem,
						lastTriggeredFrom,
						triggerMethod,
						aiGlobalOptIn,
						locale,
					});

					openAIModalCommand(editorView.state, editorView.dispatch);
				}}
			>
				{!isIconOnly &&
					(emptyStateHeroConfigItem ? (
						<Resizing
							dimension="width"
							duration={token('motion.duration.medium')}
							easing={token('motion.easing.inout.bold')}
						>
							{({ ref }) => (
								<Box as="span" ref={ref} xcss={rovoButtonStyles.heroLabelWrapper}>
									{title}
								</Box>
							)}
						</Resizing>
					) : (
						title
					))}
			</ToolbarButton>
		);

		return isIconOnly ? (
			<ToolbarTooltip content={title}>{heroPromptButton}</ToolbarTooltip>
		) : (
			heroPromptButton
		);
	}

	const IconComponent = configItem.icon ? configItem.icon({ formatMessage }) : null;

	return (
		<ToolbarButton
			isDisabled={isDisabled}
			iconBefore={IconComponent}
			label={title}
			testId={testId}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
				if (!editorView) {
					return;
				}
				const state = editorView.state;
				const { selection } = state;
				const selectionCategory =
					selection && getSelectionCategoryUsingState(state, [selection.from, selection.to]);
				const hasPartialNonDocumentSelection =
					selectionCategory !== SELECTION_CATEGORY.EMPTY &&
					selectionCategory !== SELECTION_CATEGORY.FULL_DOCUMENT;

				const isImproveDescriptionHero = resolvedHero.config.key === CONFIG_ITEM_KEYS.ADD_STRUCTURE;

				const resolvedConfigItem =
					isImproveDescriptionHero &&
					hasPartialNonDocumentSelection &&
					fg('use_improve_writing_on_partial_text_selection')
						? improveWriting()
						: resolvedHero.config;

				const lastTriggeredFrom = onPrimaryToolbar ? 'mainToolbar' : 'floatingToolbar';
				const isKeyboardTrigger = event?.nativeEvent?.detail === 0;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';

				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem: resolvedConfigItem,
					lastTriggeredFrom,
					triggerMethod,
					aiGlobalOptIn,
					locale,
				});

				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{!isIconOnly && title}
		</ToolbarButton>
	);
};

// Legacy version with conditional logic (will be removed when platform_editor_ai_aifc_streaming is cleaned up)
export const ImproveWritingButton = ({
	aiGlobalOptIn,
	parents,
	shouldShowImproveDescription,
	getAdditionalProps,
	api,
	isIconOnly = false,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	getAdditionalProps?: () => Record<string, unknown>;
	isIconOnly?: boolean;
	parents: ToolbarComponentTypes;
	shouldShowImproveDescription?: boolean;
}): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { editorView } = useEditorToolbar();
	const disabled = useIsAiEntryPointDisabled(api);
	const hasVisibleContent = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) => states.aiExperienceState?.hasVisibleContent,
	);

	const isInDocumentStreamingEnabled = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) =>
			states.aiExperienceState?.aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
	);

	useToolbarButtonViewedAnalytics({
		api,
		state: editorView?.state,
		enabled: isInDocumentStreamingEnabled,
		actionSubjectId: ACTION_SUBJECT_ID.AI_HERO_PROMPT_BUTTON,
	});

	const onPrimaryToolbar = parents.at(0)?.key === TOOLBARS.PRIMARY_TOOLBAR;
	const onJiraDescriptionPrimaryToolbar = shouldShowImproveDescription && onPrimaryToolbar;

	const title = formatMessage(
		onJiraDescriptionPrimaryToolbar
			? aiConfigItemMessages.improveDescription
			: aiMessages.improveWritingToolbarIconTitle,
	);
	const testId = onJiraDescriptionPrimaryToolbar
		? 'editor-toolbar__improve-description'
		: 'editor-toolbar__improve-writing';

	const isDisabled = disabled || !hasVisibleContent;

	return (
		<ToolbarButton
			isDisabled={isDisabled}
			iconBefore={<AICommandIcon label={title} size="small" />}
			testId={testId}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
				if (!editorView) {
					return;
				}
				const configItem = onJiraDescriptionPrimaryToolbar
					? createImproveDescription({
							getAdditionalProps: getAdditionalProps as GetAdditionalProps,
						})
					: improveWriting();
				const lastTriggeredFrom = onPrimaryToolbar ? 'mainToolbar' : 'floatingToolbar';
				const isKeyboardTrigger = event?.nativeEvent?.detail === 0;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';

				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem,
					lastTriggeredFrom,
					triggerMethod,
					aiGlobalOptIn,
					locale,
				});

				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{!isIconOnly && title}
		</ToolbarButton>
	);
};

const RovoMenu = ({
	children,
	isNewToolbarEnabled,
	api,
	parents,
	testId,
	isInDocumentStreamingEnabled,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	children: React.ReactNode;
	isInDocumentStreamingEnabled?: boolean;
	isNewToolbarEnabled: boolean;
	parents: ToolbarComponentTypes;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	const { editorView } = useEditorToolbar();
	const hasVisibleContent = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) => states.aiExperienceState?.hasVisibleContent,
	);
	const disabled = useIsAiEntryPointDisabled(api);
	const isDisabled = disabled || !hasVisibleContent;

	const onClick = (_event: React.MouseEvent<HTMLButtonElement>, isOpen: boolean) => {
		if (!editorView) {
			return;
		}
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_MORE_ROVO_OPTIONS,
			eventType: EVENT_TYPE.UI,
			attributes: {
				inputMethod: getInputMethodFromParentKeys(parents),
				opened: isOpen,
			},
		});
	};

	return (
		<RovoSplitButtonWrapper
			isNewToolbarEnabled={isNewToolbarEnabled}
			isInDocumentStreamingEnabled={isInDocumentStreamingEnabled}
		>
			<ToolbarDropdownMenuProvider>
				<ToolbarDropdownMenu
					iconBefore={<MoreItemsIcon label={formatMessage(aiMessages.moreRovoOptionsMenuLabel)} />}
					testId={testId ?? 'editor-toolbar__ask-rovo-menu'}
					isDisabled={isDisabled}
					onClick={onClick}
				>
					{children}
				</ToolbarDropdownMenu>
			</ToolbarDropdownMenuProvider>
		</RovoSplitButtonWrapper>
	);
};

const RovoPromptNestedMenu = ({
	itemKey,
	icon,
	messageDescriptor,
	children,
	api,
	parents,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	icon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	itemKey: string;
	messageDescriptor: MessageDescriptor;
	parents: ToolbarComponentTypes;
}) => {
	const { formatMessage } = useIntl();
	const Icon = icon as React.ComponentType<NewCoreIconProps>;

	return (
		<ToolbarNestedDropdownMenu
			elemBefore={
				<Icon
					size="small"
					label={
						expValEquals('jira_editor_a11y_toolbar_fixes', 'isEnabled', true)
							? ''
							: formatMessage(messageDescriptor)
					}
				/>
			}
			text={formatMessage(messageDescriptor)}
			elemAfter={
				<NestedDropdownRightIcon
					label={
						expValEquals('jira_editor_a11y_toolbar_fixes', 'isEnabled', true)
							? ''
							: formatMessage(messageDescriptor)
					}
				/>
			}
			testId={`dropdown-item__${itemKey}`}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={() => {
				const actionSubjectId =
					itemKey === CHANGE_TONE_NESTED_MENU.key
						? ACTION_SUBJECT_ID.AI_CHANGE_TONE
						: itemKey === TRANSLATE_NESTED_MENU.key
							? ACTION_SUBJECT_ID.AI_TRANSLATE
							: undefined;

				if (!actionSubjectId) {
					return;
				}

				api?.analytics?.actions.fireAnalyticsEvent({
					action: ACTION.CLICKED,
					actionSubject: ACTION_SUBJECT.TOOLBAR_DROPDOWN_MENU_ITEM,
					actionSubjectId,
					eventType: EVENT_TYPE.UI,
					attributes: {
						inputMethod: getInputMethodFromParentKeys(parents),
					},
				});
			}}
		>
			{children}
		</ToolbarNestedDropdownMenu>
	);
};

const RovoPromptItem = ({
	api,
	aiGlobalOptIn,
	itemKey,
	icon,
	messageDescriptor,
	configItem,
	parents,
	onClick,
	triggeredFor,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem?: EditorPluginAIConfigItem;
	icon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	itemKey: string;
	messageDescriptor: MessageDescriptor;
	onClick?: () => void;
	parents: ToolbarComponentTypes;
	triggeredFor?: NodeType;
}) => {
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();
	const isDisabled = useIsAiEntryPointDisabled(api);

	const Icon = icon as React.ComponentType<NewCoreIconProps>;

	const onPrimaryToolbar = parents.at(0)?.key === TOOLBARS.PRIMARY_TOOLBAR;

	return (
		<ToolbarDropdownItem
			elemBefore={
				<Icon
					size="small"
					label={
						expValEquals('jira_editor_a11y_toolbar_fixes', 'isEnabled', true)
							? ''
							: formatMessage(messageDescriptor)
					}
				/>
			}
			elemAfter={<div />}
			testId={`dropdown-item__${itemKey}`}
			isDisabled={isDisabled}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={(event) => {
				if (onClick) {
					return onClick();
				}
				if (!editorView) {
					return;
				}
				const isKeyboardTrigger = event?.nativeEvent?.detail === 0;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem: configItem as EditorPluginAIConfigItem,
					lastTriggeredFrom: onPrimaryToolbar ? 'primaryToolbar' : 'floatingToolbar',
					triggerMethod,
					aiGlobalOptIn,
					locale,
					triggeredFor,
				});

				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{formatMessage(messageDescriptor)}
		</ToolbarDropdownItem>
	);
};

export const AIToolbarComponents = ({
	editorPluginAIProvider,
	aiGlobalOptIn,
	shouldShowImproveDescription,
	getAdditionalProps,
	isNewToolbarEnabled,
	api,
	isRovoAvailable,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	getAdditionalProps?: () => Record<string, unknown>;
	isNewToolbarEnabled: boolean;
	isRovoAvailable?: boolean;
	shouldShowImproveDescription?: boolean;
}): RegisterComponent[] => {
	const breakpointPreset = api?.toolbar?.actions.getBreakpointPreset();

	const heroGroupKey =
		editorPluginAIProvider.heroConfigItem &&
		(editorPluginAIProvider.isInDocumentStreamingEnabled || fg('draft_reply_contextual_pills_gate'))
			? HERO_PROMPT_BUTTON_GROUP.key
			: IMPROVE_WRITING_GROUP.key;

	return [
		// AI SECTION
		{
			type: AI_SECTION.type,
			key: AI_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.INLINE_TEXT_TOOLBAR,
					rank: TOOLBAR_RANK[AI_SECTION.key],
				},
			],
			component: ({ children }) => (
				<AISection api={api} hasSeparator={SeparatorPosition.END}>
					{children}
				</AISection>
			),
		},
		// AI SECTION FOR FULL PAGE PRIMARY TOOLBAR
		{
			type: AI_SECTION.type,
			key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
			parents: [
				...(!editorPluginAIProvider.hidePrimaryToolbarButton &&
				!editorPluginAIProvider.isInDocumentStreamingEnabled &&
				// when platform_editor_toolbar_aifc experiment is running, the test cohort should receive the same AI button layout
				!(
					!editorPluginAIProvider.isInDocumentStreamingEnabled &&
					editorExperiment('platform_editor_toolbar_aifc', true)
				)
					? [
							{
								type: 'toolbar',
								key: TOOLBARS.PRIMARY_TOOLBAR,
								rank: TOOLBAR_RANK[AI_SECTION.key],
							} as const,
						]
					: []),
			],
			component: ({ children }) => (
				<PrimaryToolbarAISection api={api} editorAppearance="full-page">
					{children}
				</PrimaryToolbarAISection>
			),
		},
		// AI SECTION FOR COMMENT PRIMARY TOOLBAR
		{
			type: AI_SECTION.type,
			key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
			parents: [
				...(!editorPluginAIProvider.hidePrimaryToolbarButton
					? [
							{
								type: 'toolbar',
								key: TOOLBARS.PRIMARY_TOOLBAR,
								rank: TOOLBAR_RANK[AI_SECTION.key],
							} as const,
						]
					: []),
			],
			component: ({ children }) => (
				<PrimaryToolbarAISection api={api} editorAppearance="comment">
					{children}
				</PrimaryToolbarAISection>
			),
		},
		// AI SECTION FOR FULL PAGE PRIMARY TOOLBAR AIFC (only Rovo button with "Write")
		{
			type: AI_SECTION_PRIMARY_TOOLBAR.type,
			key: AI_SECTION_PRIMARY_TOOLBAR.key,
			parents: [
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning
				...(!editorPluginAIProvider.hidePrimaryToolbarButton &&
				editorPluginAIProvider.isInDocumentStreamingEnabled &&
				// when platform_editor_toolbar_aifc experiment is running, the test cohort should receive the same AI button layout
				!(
					!editorPluginAIProvider.isInDocumentStreamingEnabled &&
					editorExperiment('platform_editor_toolbar_aifc', true)
				)
					? [
							{
								type: 'toolbar',
								key: TOOLBARS.PRIMARY_TOOLBAR,
								rank: TOOLBAR_RANK[AI_SECTION.key],
							} as const,
						]
					: []),
			],
			component: ({ children }) => (
				<PrimaryToolbarAISection api={api} editorAppearance="full-page" hasSeparator={true}>
					{children}
				</PrimaryToolbarAISection>
			),
		},
		// AI VIEW SECTION
		{
			type: AI_VIEW_SECTION.type,
			key: AI_VIEW_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.INLINE_TEXT_TOOLBAR,
					rank: TOOLBAR_RANK[AI_VIEW_SECTION.key],
				},
			],
			component: ({ children }) => (
				<AIViewSection api={api} hasSeparator={SeparatorPosition.END}>
					{children}
				</AIViewSection>
			),
		},
		{
			type: AI_VIEW_SECTION.type,
			key: AI_VIEW_SECTION.key,
			parents: [
				...(editorPluginAIProvider.hidePrimaryToolbarButton
					? []
					: [
							{
								type: 'toolbar',
								key: TOOLBARS.PRIMARY_TOOLBAR,
								rank: TOOLBAR_RANK[AI_VIEW_SECTION.key],
							} as const,
						]),
			],
			component: ({ children }) => <AIViewSection api={api}>{children}</AIViewSection>,
		},
		// ROVO GROUP
		{
			type: ROVO_GROUP.type,
			key: ROVO_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION.key,
					// when platform_editor_toolbar_aifc experiment is running, the test cohort should receive the same AI button layout
					rank:
						!editorPluginAIProvider.isInDocumentStreamingEnabled &&
						editorExperiment('platform_editor_toolbar_aifc', true)
							? 400
							: AI_SECTION_RANK[ROVO_GROUP.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[ROVO_GROUP.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[ROVO_GROUP.key],
				},
			],
			component: ({ children }) => (
				<Show above={breakpointPreset === 'jsm-comment' ? 'sm' : 'lg'}>
					<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
				</Show>
			),
		},
		// ROVO GROUP DISPLAY SMALL
		{
			type: ROVO_GROUP_DISPLAY_SMALL.type,
			key: ROVO_GROUP_DISPLAY_SMALL.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION.key,
					rank: AI_SECTION_RANK[ROVO_GROUP_DISPLAY_SMALL.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[ROVO_GROUP_DISPLAY_SMALL.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[ROVO_GROUP_DISPLAY_SMALL.key],
				},
			],
			component: ({ children }) => (
				<Show below={breakpointPreset === 'jsm-comment' ? 'sm' : 'lg'}>
					<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
				</Show>
			),
		},
		// ROVO HERO BUTTON
		{
			type: ROVO_HERO_BUTTON.type,
			key: ROVO_HERO_BUTTON.key,
			parents: [
				...(isRovoAvailable
					? [
							{
								type: ROVO_GROUP.type,
								key: ROVO_GROUP.key,
								rank: ROVO_GROUP_RANK[ROVO_HERO_BUTTON.key],
							},
							{
								type: ROVO_GROUP_DISPLAY_SMALL.type,
								key: ROVO_GROUP_DISPLAY_SMALL.key,
								rank: ROVO_GROUP_DISPLAY_SMALL_RANK[ROVO_HERO_BUTTON.key],
							},
						]
					: []),
			],

			component: ({ parents }) => (
				<RovoButton
					editorPluginAIProvider={editorPluginAIProvider}
					aiGlobalOptIn={aiGlobalOptIn}
					parents={parents}
					isNewToolbarEnabled={isNewToolbarEnabled}
					api={api}
				/>
			),
		},
		// ROVO GROUP FOR PRIMARY TOOLBAR AIFC
		{
			type: ROVO_GROUP_PRIMARY_TOOLBAR.type,
			key: ROVO_GROUP_PRIMARY_TOOLBAR.key,
			parents: [
				{
					type: AI_SECTION_PRIMARY_TOOLBAR.type,
					key: AI_SECTION_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[ROVO_GROUP_PRIMARY_TOOLBAR.key],
				},
			],
			component: ({ children }) => <ToolbarButtonGroup>{children}</ToolbarButtonGroup>,
		},
		{
			type: ROVO_WRITE_BUTTON_PRIMARY_TOOLBAR.type,
			key: ROVO_WRITE_BUTTON_PRIMARY_TOOLBAR.key,
			parents: [
				...(isRovoAvailable
					? [
							{
								type: ROVO_GROUP_PRIMARY_TOOLBAR.type,
								key: ROVO_GROUP_PRIMARY_TOOLBAR.key,
								rank: ROVO_GROUP_RANK[ROVO_WRITE_BUTTON_PRIMARY_TOOLBAR.key],
							},
						]
					: []),
			],

			component: ({ parents }) => (
				<RovoButton
					editorPluginAIProvider={editorPluginAIProvider}
					aiGlobalOptIn={aiGlobalOptIn}
					parents={parents}
					isNewToolbarEnabled={isNewToolbarEnabled}
					api={api}
				/>
			),
		},
		{
			type: ROVO_MENU.type,
			key: ROVO_MENU.key,
			parents: [
				// when platform_editor_toolbar_aifc experiment is running, the test cohort should receive the same AI button layout
				...(!editorPluginAIProvider.isInDocumentStreamingEnabled &&
				editorExperiment('platform_editor_toolbar_aifc', true)
					? []
					: isRovoAvailable
						? [
								{
									type: ROVO_GROUP.type,
									key: ROVO_GROUP.key,
									rank: ROVO_GROUP_RANK[ROVO_MENU.key],
								},
							]
						: [
								{
									type: 'group' as const,
									key: heroGroupKey,
									rank: ROVO_GROUP_RANK[ROVO_MENU.key],
								},
							]),
			],

			component: ({ children, parents }) => (
				<RovoMenu
					api={api}
					isNewToolbarEnabled={isNewToolbarEnabled}
					isInDocumentStreamingEnabled={editorPluginAIProvider.isInDocumentStreamingEnabled}
					parents={parents}
					testId="editor-toolbar__ask-rovo-menu"
				>
					{children}
				</RovoMenu>
			),
		},
		{
			type: ROVO_PROMPT_MENU_SECTION.type,
			key: ROVO_PROMPT_MENU_SECTION.key,
			parents: [
				{
					type: ROVO_MENU.type,
					key: ROVO_MENU.key,
					rank: ROVO_MENU_RANK[ROVO_PROMPT_MENU_SECTION.key],
				},
			],
		},
		// ROVO MENU DISPLAY SMALL + MENU SECTION
		{
			type: ROVO_MENU_DISPLAY_SMALL.type,
			key: ROVO_MENU_DISPLAY_SMALL.key,
			parents: [
				// when platform_editor_toolbar_aifc experiment is running, the test cohort should receive the same AI button layout
				...(!editorPluginAIProvider.isInDocumentStreamingEnabled &&
				editorExperiment('platform_editor_toolbar_aifc', true)
					? []
					: [
							{
								type: ROVO_GROUP_DISPLAY_SMALL.type,
								key: ROVO_GROUP_DISPLAY_SMALL.key,
								rank: ROVO_GROUP_DISPLAY_SMALL_RANK[ROVO_MENU_DISPLAY_SMALL.key],
							},
						]),
			],

			component: ({ children, parents }) => (
				<RovoMenu
					api={api}
					isNewToolbarEnabled={isNewToolbarEnabled}
					parents={parents}
					testId="editor-toolbar__ask-rovo-menu-display-small"
				>
					{children}
				</RovoMenu>
			),
		},
		{
			type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
			key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
			parents: [
				{
					type: ROVO_MENU_DISPLAY_SMALL.type,
					key: ROVO_MENU_DISPLAY_SMALL.key,
					rank: ROVO_MENU_RANK[ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key],
				},
			],
		},
		// FROM HERE ONWARDS ARE MENU ITEMS
		...((editorPluginAIProvider.heroConfigItem &&
		editorPluginAIProvider.isInDocumentStreamingEnabled
			? [
					// New dynamic implementation: uses heroConfigItem
					{
						type: HERO_PROMPT_MENU_ITEM.type,
						key: HERO_PROMPT_MENU_ITEM.key,
						parents: [
							{
								type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
								key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
								rank: ROVO_PROMPT_MENU_SECTION_RANK[HERO_PROMPT_MENU_ITEM.key],
							},
						],
						component: ({ parents }: { parents: ToolbarComponentTypes }) => {
							if (!editorPluginAIProvider.heroConfigItem) {
								return null;
							}

							const configItem = editorPluginAIProvider.heroConfigItem.config;

							return (
								<RovoPromptItem
									api={api}
									aiGlobalOptIn={aiGlobalOptIn}
									itemKey={HERO_PROMPT_MENU_ITEM.key}
									icon={createHeroPromptIcon(configItem)}
									messageDescriptor={configItem.title}
									configItem={configItem}
									parents={parents}
								/>
							);
						},
					},
				]
			: editorPluginAIProvider.isInDocumentStreamingEnabled
				? [
						// Previous implementation: hardcoded improve writing
						{
							type: IMPROVE_WRITING_MENU_ITEM.type,
							key: IMPROVE_WRITING_MENU_ITEM.key,
							parents: [
								{
									type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
									key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
									rank: ROVO_PROMPT_MENU_SECTION_RANK[IMPROVE_WRITING_MENU_ITEM.key],
								},
							],
							component: ({ parents }) => (
								<RovoPromptItem
									api={api}
									aiGlobalOptIn={aiGlobalOptIn}
									itemKey={IMPROVE_WRITING_MENU_ITEM.key}
									icon={AICommandIcon}
									messageDescriptor={aiMessages.improveWritingToolbarIconTitle}
									configItem={improveWriting()}
									parents={parents}
								/>
							),
						},
					]
				: []) as RegisterComponent[]),
		{
			type: SPELLING_AND_GRAMMAR_MENU_ITEM.type,
			key: SPELLING_AND_GRAMMAR_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[SPELLING_AND_GRAMMAR_MENU_ITEM.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[SPELLING_AND_GRAMMAR_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={SPELLING_AND_GRAMMAR_MENU_ITEM.key}
					icon={AISpellcheckIcon}
					messageDescriptor={aiMessages.spellingAndGrammarToolbarDropdownIconTitle}
					configItem={fixSpellingAndGrammar()}
					parents={parents}
				/>
			),
		},
		{
			type: CHANGE_TONE_NESTED_MENU.type,
			key: CHANGE_TONE_NESTED_MENU.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[CHANGE_TONE_NESTED_MENU.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[CHANGE_TONE_NESTED_MENU.key],
				},
			],
			component: ({ children, parents }) => (
				<RovoPromptNestedMenu
					itemKey={CHANGE_TONE_NESTED_MENU.key}
					icon={AIChangeToneIcon}
					messageDescriptor={aiMessages.changeToneToolbarDropdownIconTitle}
					api={api}
					parents={parents}
				>
					{children}
				</RovoPromptNestedMenu>
			),
		},
		{
			type: CHANGE_TONE_MENU_SECTION.type,
			key: CHANGE_TONE_MENU_SECTION.key,
			parents: [
				{
					type: CHANGE_TONE_NESTED_MENU.type,
					key: CHANGE_TONE_NESTED_MENU.key,
					rank: CHANGE_TONE_MENU_RANK[CHANGE_TONE_MENU_SECTION.key],
				},
			],
		},
		{
			type: MORE_PROFESSIONAL_MENU_ITEM.type,
			key: MORE_PROFESSIONAL_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_PROFESSIONAL_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={MORE_PROFESSIONAL_MENU_ITEM.key}
					icon={AIBriefcaseIcon}
					messageDescriptor={aiMessages.moreProfessionalToneToolbarDropdownIconTitle}
					configItem={changeToneToProfessional()}
					parents={parents}
				/>
			),
		},
		{
			type: MORE_CASUAL_MENU_ITEM.type,
			key: MORE_CASUAL_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_CASUAL_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={MORE_CASUAL_MENU_ITEM.key}
					icon={AICasualIcon}
					messageDescriptor={aiMessages.moreCasualToneToolbarDropdownIconTitle}
					configItem={changeToneToCasual()}
					parents={parents}
				/>
			),
		},
		{
			type: MORE_EMPATHETIC_MENU_ITEM.type,
			key: MORE_EMPATHETIC_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_EMPATHETIC_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={MORE_EMPATHETIC_MENU_ITEM.key}
					icon={AIHeartIcon}
					messageDescriptor={aiMessages.moreEmpatheticToneToolbarDropdownIconTitle}
					configItem={changeToneToEmpathetic()}
					parents={parents}
				/>
			),
		},
		{
			type: MAKE_SHORTER_MENU_ITEM.type,
			key: MAKE_SHORTER_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_SHORTER_MENU_ITEM.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_SHORTER_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={MAKE_SHORTER_MENU_ITEM.key}
					icon={AIShortenIcon}
					messageDescriptor={aiMessages.makeShorterToolbarDropdownIconTitle}
					configItem={makeShorter()}
					parents={parents}
				/>
			),
		},
		{
			type: MAKE_LONGER_MENU_ITEM.type,
			key: MAKE_LONGER_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_LONGER_MENU_ITEM.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_LONGER_MENU_ITEM.key],
				},
			],
			component: ({ parents }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={MAKE_LONGER_MENU_ITEM.key}
					icon={AILengthenIcon}
					messageDescriptor={aiMessages.makeLongerToolbarDropdownIconTitle}
					configItem={makeLonger()}
					parents={parents}
				/>
			),
		},
		{
			type: TRANSLATE_NESTED_MENU.type,
			key: TRANSLATE_NESTED_MENU.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[TRANSLATE_NESTED_MENU.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[TRANSLATE_NESTED_MENU.key],
				},
			],
			component: ({ children, parents }) => (
				<RovoPromptNestedMenu
					itemKey={TRANSLATE_NESTED_MENU.key}
					icon={AITranslateIcon}
					messageDescriptor={aiMessages.translateOptionsIconLabel}
					api={api}
					parents={parents}
				>
					{children}
				</RovoPromptNestedMenu>
			),
		},
		{
			type: TRANSLATE_MENU_SECTION.type,
			key: TRANSLATE_MENU_SECTION.key,
			parents: [
				{
					type: TRANSLATE_NESTED_MENU.type,
					key: TRANSLATE_NESTED_MENU.key,
					rank: TRANSLATE_MENU_RANK[TRANSLATE_MENU_SECTION.key],
				},
			],
		},
		// Dynamic translation language menu items
		...getTranslationLanguagesToolbarItems(api, aiGlobalOptIn),
		// DEFINE MENU ITEM
		...((editorPluginAIProvider.isInDocumentStreamingEnabled
			? [
					{
						type: DEFINE_MENU_ITEM.type,
						key: DEFINE_MENU_ITEM.key,
						parents: [
							{
								type: ROVO_PROMPT_MENU_SECTION.type,
								key: ROVO_PROMPT_MENU_SECTION.key,
								rank: ROVO_PROMPT_MENU_SECTION_RANK[DEFINE_MENU_ITEM.key],
							},
							{
								type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
								key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
								rank: ROVO_PROMPT_MENU_SECTION_RANK[DEFINE_MENU_ITEM.key],
							},
						],
						component: ({ parents }) => {
							if (!Boolean(api?.aiDefinitions)) {
								return null;
							}
							return (
								<RovoPromptItem
									api={api}
									aiGlobalOptIn={aiGlobalOptIn}
									itemKey={DEFINE_MENU_ITEM.key}
									icon={InformationCircleIcon}
									messageDescriptor={aiMessages.DefineDropdownIconTitle}
									// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
									onClick={() => {
										api?.aiDefinitions?.actions.openReadingAidsPopup();
									}}
									parents={parents}
								/>
							);
						},
					},
				]
			: []) as RegisterComponent[]),
		// HERO BUTTON (with its own group based on heroConfigItem or legacy implementation)
		...((isNewToolbarEnabled &&
		editorPluginAIProvider.heroConfigItem &&
		(editorPluginAIProvider.isInDocumentStreamingEnabled || fg('draft_reply_contextual_pills_gate'))
			? [
					// New dynamic implementation: uses heroConfigItem
					{
						type: HERO_PROMPT_BUTTON_GROUP.type,
						key: HERO_PROMPT_BUTTON_GROUP.key,
						parents: [
							{
								type: AI_SECTION.type,
								key: AI_SECTION.key,
								rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key],
							},
							{
								type: AI_SECTION.type,
								key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
								rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key],
							},
							{
								type: AI_SECTION.type,
								key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
								rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key],
							},
						],
						component: ({ children }: { children: React.ReactNode }) => (
							<Show above={breakpointPreset === 'jsm-comment' ? 'sm' : 'lg'}>
								<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
							</Show>
						),
					},
					{
						type: HERO_PROMPT_BUTTON.type,
						key: HERO_PROMPT_BUTTON.key,
						parents: [
							{
								type: HERO_PROMPT_BUTTON_GROUP.type,
								key: HERO_PROMPT_BUTTON_GROUP.key,
								rank: HERO_PROMPT_BUTTON_GROUP_RANK[HERO_PROMPT_BUTTON.key],
							},
							...(!isRovoAvailable ||
							(Boolean(editorPluginAIProvider.emptyStateHeroConfigItem) &&
								expValNoExposure('jira-smart-replies-v2', 'isEnabled', false))
								? [
										{
											type: ROVO_GROUP_DISPLAY_SMALL.type,
											key: ROVO_GROUP_DISPLAY_SMALL.key,
											rank: !isRovoAvailable
												? ROVO_GROUP_DISPLAY_SMALL_RANK[ROVO_HERO_BUTTON.key]
												: ROVO_GROUP_DISPLAY_SMALL_RANK[HERO_PROMPT_BUTTON.key],
										},
									]
								: []),
						],
						component: ({ parents }: { parents: ToolbarComponentTypes }) =>
							editorPluginAIProvider.heroConfigItem ? (
								<HeroPromptButton
									aiGlobalOptIn={aiGlobalOptIn}
									parents={parents}
									api={api}
									heroConfigItem={editorPluginAIProvider.heroConfigItem}
									emptyStateHeroConfigItem={editorPluginAIProvider.emptyStateHeroConfigItem}
									isIconOnly={parents.some((parent) => parent.key === ROVO_GROUP_DISPLAY_SMALL.key)}
								/>
							) : null,
					},
				]
			: isNewToolbarEnabled
				? [
						{
							type: 'group',
							key: 'improve-writing-group',
							parents: [
								{
									type: AI_SECTION.type,
									key: AI_SECTION.key,
									rank: AI_SECTION_RANK[IMPROVE_WRITING_GROUP.key],
								},
								{
									type: AI_SECTION.type,
									key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
									rank: AI_SECTION_RANK[IMPROVE_WRITING_GROUP.key],
								},
								{
									type: AI_SECTION.type,
									key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
									rank: AI_SECTION_RANK[IMPROVE_WRITING_GROUP.key],
								},
							],
							component: ({ children }: { children: React.ReactNode }) => (
								<Show above={breakpointPreset === 'jsm-comment' ? 'sm' : 'lg'}>
									<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
								</Show>
							),
						},
						{
							type: IMPROVE_WRITING_BUTTON.type,
							key: IMPROVE_WRITING_BUTTON.key,
							parents: [
								{
									type: IMPROVE_WRITING_GROUP.type,
									key: IMPROVE_WRITING_GROUP.key,
									rank: IMPROVE_WRITING_GROUP_RANK[IMPROVE_WRITING_BUTTON.key],
								},
								...(!isRovoAvailable
									? [
											{
												type: ROVO_GROUP_DISPLAY_SMALL.type,
												key: ROVO_GROUP_DISPLAY_SMALL.key,
												rank: ROVO_GROUP_DISPLAY_SMALL_RANK[ROVO_HERO_BUTTON.key],
											},
										]
									: []),
							],
							component: ({ parents }: { parents: ToolbarComponentTypes }) => (
								<ImproveWritingButton
									aiGlobalOptIn={aiGlobalOptIn}
									parents={parents}
									shouldShowImproveDescription={shouldShowImproveDescription}
									getAdditionalProps={getAdditionalProps}
									api={api}
									isIconOnly={parents.some((parent) => parent.key === ROVO_GROUP_DISPLAY_SMALL.key)}
								/>
							),
						},
					]
				: []) as RegisterComponent[]),
		...(isNewToolbarEnabled &&
		editorPluginAIProvider.isInDocumentStreamingEnabled &&
		expValEqualsNoExposure('platform_editor_ai_improve_formatting_toolbar', 'isEnabled', true)
			? [
					{
						type: HERO_PROMPT_BUTTON_GROUP.type,
						key: 'hero-prompt-button-group-primary',
						parents: [
							{
								type: AI_SECTION_PRIMARY_TOOLBAR.type,
								key: AI_SECTION_PRIMARY_TOOLBAR.key,
								rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key],
							},
						],
						component: ({ children }: { children: React.ReactNode }) => (
							<Show above="lg">
								<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
							</Show>
						),
					},
					{
						type: HERO_PROMPT_BUTTON.type,
						key: 'hero-prompt-button-primary',
						parents: [
							{
								type: HERO_PROMPT_BUTTON_GROUP.type,
								key: 'hero-prompt-button-group-primary',
								rank: HERO_PROMPT_BUTTON_GROUP_RANK[HERO_PROMPT_BUTTON.key],
							},
						],
						component: ({ parents }: { parents: ToolbarComponentTypes }) => {
							// eslint-disable-next-line react-hooks/rules-of-hooks
							const heroConfigItem = React.useMemo<EditorPluginAIConfigItemWithOptions>(
								() => ({
									config: {
										...addPolish(),
										icon: () => <AiGenerativeCleanupIcon label="" size="small" />,
									},
									triggers: addPolishWithOptions({ isInDocumentStreamingEnabled: true }).triggers,
								}),
								[],
							);
							return (
								<HeroPromptButton
									aiGlobalOptIn={aiGlobalOptIn}
									parents={parents}
									api={api}
									heroConfigItem={heroConfigItem}
									emptyStateHeroConfigItem={editorPluginAIProvider.emptyStateHeroConfigItem}
									isIconOnly={false}
								/>
							);
						},
					},
				]
			: []),
		{
			type: ROVO_GROUP_VIEW.type,
			key: ROVO_GROUP_VIEW.key,
			parents: [
				{
					type: AI_VIEW_SECTION.type,
					key: AI_VIEW_SECTION.key,
					rank: AI_SECTION_RANK[ROVO_GROUP.key],
				},
			],
		},
		{
			type: ROVO_HERO_BUTTON.type,
			key: ROVO_HERO_BUTTON.key,
			parents: [
				{
					type: ROVO_GROUP_VIEW.type,
					key: ROVO_GROUP_VIEW.key,
					rank: ROVO_GROUP_RANK[ROVO_HERO_BUTTON.key],
				},
			],
			component: () => (
				<AskRovoChatButton api={api} editorPluginAIProvider={editorPluginAIProvider} />
			),
		},
		// DEFINE GROUP + DEFINE BUTTON
		{
			type: DEFINE_GROUP.type,
			key: DEFINE_GROUP.key,
			parents: [
				{
					type: AI_VIEW_SECTION.type,
					key: AI_VIEW_SECTION.key,
					rank: AI_SECTION_RANK[DEFINE_GROUP.key],
				},
			],
			component: ({ children }: { children: React.ReactNode }) => {
				if (!Boolean(api?.aiDefinitions)) {
					return null;
				}
				return api?.aiDefinitions?.actions.selectionExceedsMaxWordCount() ? undefined : (
					<Show above="lg">
						<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
					</Show>
				);
			},
		},
		{
			type: DEFINE_BUTTON.type,
			key: DEFINE_BUTTON.key,
			parents: [
				{
					type: DEFINE_GROUP.type,
					key: DEFINE_GROUP.key,
					rank: DEFINE_GROUP_RANK[DEFINE_BUTTON.key],
				},
			],
			component: () => {
				if (!Boolean(api?.aiDefinitions)) {
					return null;
				}
				return <DefineButton api={api} />;
			},
		},
		{
			type: ADD_POLISH_MENU_ITEM.type,
			key: ADD_POLISH_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[ADD_POLISH_MENU_ITEM.key],
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[ADD_POLISH_MENU_ITEM.key],
				},
			],
			component: ({ parents }: { parents: ToolbarComponentTypes }) => (
				<RovoPromptItem
					api={api}
					aiGlobalOptIn={aiGlobalOptIn}
					itemKey={ADD_POLISH_MENU_ITEM.key}
					icon={AiGenerativeCleanupIcon}
					messageDescriptor={aiConfigItemMessages.improveFormattingTitle}
					configItem={addPolish()}
					parents={parents}
				/>
			),
		},
	];
};

const getTranslationLanguagesToolbarItems = (
	api: ExtractInjectionAPI<AIPlugin> | undefined,
	aiGlobalOptIn: AIGlobalOptIn,
): RegisterComponent[] => {
	const translationConfigItemsMap = createTranslationConfigItems();
	return getSortedSupportedTranslationLanguages().map((lang, index) => ({
		type: 'menu-item' as const,
		key: `${lang}-language-menu-item`,
		parents: [
			{
				type: TRANSLATE_MENU_SECTION.type,
				key: TRANSLATE_MENU_SECTION.key,
				rank: (index + 1) * 100,
			},
		],
		component: ({ parents }: { parents: ToolbarComponentTypes }) => (
			<RovoPromptItem
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				itemKey={`${lang}-language-menu-item`}
				icon={AITranslateIcon}
				messageDescriptor={aiLanguageMessages[lang]}
				configItem={translationConfigItemsMap[lang]}
				parents={parents}
			/>
		),
	}));
};

const createHeroPromptIcon = (configItem: EditorPluginAIConfigItem) => {
	const IconComponent = Object.assign(
		() => {
			const { formatMessage } = useIntl();
			if (configItem.icon) {
				const iconElement = configItem.icon({ formatMessage });
				if (iconElement) {
					return iconElement;
				}
			}
			return <AICommandIcon label={formatMessage(configItem.title)} size="small" />;
		},
		{ displayName: 'HeroPromptIcon' },
	);
	return IconComponent;
};
